import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import NavBar from "../navbar";
import { validateLoggedIn } from "../utils";
import { STATUSES, STATUS_LABELS, fetchTickets, formatDate } from "./support_api";

const PAGE_SIZE = 50;

const statusColor = (status) => (status === "open" ? "warning" : status === "answered" ? "success" : "default");

const SupportTickets = () => {
    const [status, setStatus] = useState("open");
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) {
            setError("You are not logged in.");
            return undefined;
        }
        let active = true;
        setError(null);
        fetchTickets({ status, q: search, limit: PAGE_SIZE, offset })
            .then((result) => {
                if (active) setData(result);
            })
            .catch((err) => {
                Sentry.captureException(err);
                if (active) setError(err.message);
            });
        return () => {
            active = false;
        };
    }, [status, search, offset, reloadKey]);

    const submitSearch = (event) => {
        event.preventDefault();
        setOffset(0);
        setSearch(query.trim());
    };

    const total = data ? data.total : 0;
    const pageEnd = Math.min(offset + PAGE_SIZE, total);

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
                    Support Tickets
                </Typography>

                <Stack component="form" onSubmit={submitSearch} direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(event) => {
                            setStatus(event.target.value);
                            setOffset(0);
                        }}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {STATUSES.map((value) => (
                            <MenuItem key={value} value={value}>
                                {STATUS_LABELS[value]}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Search by email or subject"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        sx={{ minWidth: 320 }}
                    />
                    <Button type="submit" variant="contained">
                        Search
                    </Button>
                    <Button variant="outlined" onClick={() => setReloadKey(reloadKey + 1)}>
                        Refresh
                    </Button>
                </Stack>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!data && !error && (
                    <Box textAlign="center" p={2}>
                        <CircularProgress />
                    </Box>
                )}

                {data && (
                    <>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Last activity</TableCell>
                                        <TableCell>Created</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.items.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No tickets found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data.items.map((ticket) => (
                                        <TableRow key={ticket.id} hover>
                                            <TableCell>
                                                <Chip size="small" color={statusColor(ticket.status)} label={STATUS_LABELS[ticket.status]} />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 420, wordBreak: "break-word" }}>
                                                <Link to={`/support-tickets/${ticket.id}`}>{ticket.subject}</Link>
                                            </TableCell>
                                            <TableCell>
                                                <Link to={`/users/${ticket.user_id}/edit`}>{ticket.user_email}</Link>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {ticket.user_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{ticket.type}</TableCell>
                                            <TableCell>{ticket.category || "—"}</TableCell>
                                            <TableCell>{ticket.source}</TableCell>
                                            <TableCell>{formatDate(ticket.last_message_at)}</TableCell>
                                            <TableCell>{formatDate(ticket.created_at)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                {total === 0 ? "0 tickets" : `${offset + 1}–${pageEnd} of ${total}`}
                            </Typography>
                            <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                                Previous
                            </Button>
                            <Button size="small" disabled={pageEnd >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                                Next
                            </Button>
                        </Stack>
                    </>
                )}
            </Box>
        </>
    );
};

export default SupportTickets;
